import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { authLogin } from "@/lib/api";
import { LogoMark } from "@/components/AppLayout";

const SHOW_DEMO_HINT = process.env.REACT_APP_SHOW_DEMO_HINT === "true";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/app";

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await authLogin(email, password);
      localStorage.setItem("pm_token", data.access_token);
      localStorage.setItem("pm_user", JSON.stringify(data.user));
      navigate(from, { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Login failed. Check your credentials.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-background text-foreground flex items-center justify-center">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2 justify-center mb-8">
          <LogoMark />
          <span className="font-serif-display text-xl">ProofMark</span>
        </div>

        <div className="rounded-lg border border-border bg-card p-8">
          <h1 className="font-serif-display text-2xl mb-1">Sign in</h1>
          {SHOW_DEMO_HINT && (
            <p className="text-sm text-muted-foreground mb-6">
              Demo credentials:{" "}
              <span className="font-mono">reviewer@proofmark.io</span> /{" "}
              <span className="font-mono">demo1234</span>
            </p>
          )}

          <form onSubmit={handleSubmit} className={SHOW_DEMO_HINT ? "space-y-4" : "space-y-4 mt-6"}>
            <div>
              <label className="block text-sm font-medium mb-1.5" htmlFor="email">
                Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand/40"
                placeholder="you@proofmark.io"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5" htmlFor="password">
                Password
              </label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand/40"
                placeholder="••••••••"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-md bg-brand text-brand-foreground py-2.5 text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <div className="mt-4 text-center">
            <Link to="/forgot-password" className="text-xs text-muted-foreground hover:text-foreground transition-colors">
              Forgot your password?
            </Link>
          </div>
        </div>

        <p className="text-center text-xs text-muted-foreground mt-6">
          Not yet a customer?{" "}
          <a href="/" className="text-brand hover:underline">
            Request access
          </a>
        </p>
      </div>
    </div>
  );
}
